import { FieldKeys } from '../../../common/api/services/HistoryApi/types/fieldKeys';
import { IHistoryProps } from '../../../common/api/services/HistoryApi/types/historyTypes';
import { IncomeLossInsurance } from '../../../common/api/services/OnboardingApi/enums/OnboardingIncomeLossInsurance';
import { IBankAccount } from '../../../common/api/services/SystemInfoApi/types/ISystemInfo';
import { cantons, documentTypes, permitTypes } from '../../../common/constants/dropdownTypes';
import { countries } from '../../../common/constants/mockCountries';
import currencies from '../../../common/constants/mockCurrencies';
import { Roles } from '../../../common/enums/Roles';
import { ShownTypes } from '../../../redux/reducers/onboarding-history/types';
import { store } from '../../../redux/store';

const spendTimeOptions = ['Live in Switzerland', 'Return abroad every day', 'Have a Swiss address on weekdays'];
const maritalStatuses = ['Single', 'Married', 'Widow(er)', 'Separated', 'Divorced'];

const canton = [
    'Aargau',
    'Appenzell Innerrhoden',
    'Appenzell Ausserrhoden',
    'Bern',
    'Basel-Land',
    'Basel-Stadt',
    'Freiburg',
    'Geneva',
    'Glarus',
    'Graubünden',
    'Jura',
    'Luzern',
    'Neuenburg',
    'Nidwalden',
    'Obwalden',
    'St. Gallen',
    'Schaffhausen',
    'Solothurn',
    'Schwyz',
    'Thurgau',
    'Ticino',
    'Uri',
    'Vaud',
    'Valais',
    'Zug',
    'Zürich',
    'N/A',
];

const currency = ['EUR', 'CHF', 'GBP', 'USD'];

const basisOfLegalStayValues = [
    'Work Swiss & Live Swiss',
    'Work Swiss & Return Everyday Abroad',
    'Work Swiss & Return Weekly Abroad',
    'Work Swiss & Return Monthly Abroad',
    'Work Abroad & Live Swiss',
    'Work Abroad & Live Abroad',
];

const insurances = Object.values(IncomeLossInsurance);

export const initialHistoryFields: Record<FieldKeys, IHistoryProps> = {
    [FieldKeys.swissAddress]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'streetName',
            type: ShownTypes.input,
            label: 'Street name',
        },
        {
            key: 'streetNumber',
            type: ShownTypes.input,
            label: 'Street number',
            span: 6,
        },
        { key: 'postCode', type: ShownTypes.input, label: 'Postcode', span: 6 },
        { key: 'state', type: ShownTypes.input, label: 'Canton' },
        { key: 'city', type: ShownTypes.input, label: 'City' },
    ],
    [FieldKeys.abroadAddress]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'streetName',
            type: ShownTypes.input,
            label: 'Street name',
        },
        {
            key: 'streetNumber',
            type: ShownTypes.input,
            label: 'Street number',
            span: 6,
        },
        { key: 'postCode', type: ShownTypes.input, label: 'Postcode', span: 6 },
        { key: 'city', type: ShownTypes.input, label: 'City' },
        // { key: 'state', type: ShownTypes.input, label: 'State', span: 6 },
        {
            key: 'country',
            type: ShownTypes.select,
            options: countries.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            label: 'Country',
            span: 6,
        },
        {
            key: 'totalAttendance',
            type: ShownTypes.select,
            options: spendTimeOptions.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            label: 'How often do you visit this address?',
        },
        {
            key: 'useAsCorrespondenceLocation',
            type: ShownTypes.checkbox,
            label: `I want to receive correspondense to this address.`,
            span: 24,
        },
    ],
    [FieldKeys.maritalStatus]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'maritalStatus',
            type: ShownTypes.select,
            label: 'Marital status',
            options: maritalStatuses.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
        },
        {
            key: 'maritalStatusDetails.marriageDate',
            type: ShownTypes.date,
            label: 'Marriage date',
            condition: (values) => values.maritalStatus === 'Married',
        },
        {
            key: 'maritalStatusDetails.spouseName',
            type: ShownTypes.input,
            label: 'Spouse first name',
            condition: (values) => values.maritalStatus === 'Married',
        },
        {
            key: 'maritalStatusDetails.spouseLastName',
            type: ShownTypes.input,
            label: 'Spouse last name',
            condition: (values) => values.maritalStatus === 'Married',
        },
        {
            key: 'maritalStatusDetails.spouseDateOfBirth',
            type: ShownTypes.date,
            label: 'Spouse date of birth',
            condition: (values) => values.maritalStatus === 'Married',
        },
        {
            key: 'maritalStatusDetails.spouseNationality',
            type: ShownTypes.select,
            label: 'Spouse nationality',
            options: countries.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            condition: (values) => values.maritalStatus === 'Married',
        },
        {
            key: 'maritalStatusDetails.isSpouseSwissResident',
            type: ShownTypes.radio,
            label: 'Does your spouse live in Switzerland?',
            span: 24,
            isSeporateRadio: true,
            condition: (values) => values.maritalStatus === 'Married',
        },
        {
            key: 'maritalStatusDetails.spouseSSN',
            type: ShownTypes.input,
            label: 'Spouse AVS number',
            condition: (values) => {
                return values.maritalStatus === 'Married' && values['maritalStatusDetails.isSpouseSwissResident'];
            },
            placeholder: '756.XXXX.XXXX.XX',
        },
        {
            key: 'maritalStatusDetails.spousalIncome',
            type: ShownTypes.radio,
            label: 'Does your spouse receive an income?',
            span: 24,
            condition: (values) => values.maritalStatus === 'Married',
            popoverText: 'We need to know this information to determine your tax-at-source.',
        },
        {
            key: 'maritalStatusDetails.spouseWorkCountry',
            type: ShownTypes.select,
            label: 'Country of working',
            options: countries.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            condition: (values) => values.maritalStatus === 'Married' && values['maritalStatusDetails.spousalIncome'],
        },

        {
            key: 'maritalStatusDetails.spouseWorkCanton',
            type: ShownTypes.select,
            label: 'Working canton',
            options: canton.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            condition: (values) =>
                values.maritalStatus === 'Married' &&
                values['maritalStatusDetails.spouseWorkCountry'] === 'Switzerland' &&
                values['maritalStatusDetails.spousalIncome'],
        },
        {
            key: 'maritalStatusDetails.spouseWorkCity',
            type: ShownTypes.input,
            label: 'Working city',
            condition: (values) =>
                values.maritalStatus === 'Married' &&
                values['maritalStatusDetails.spouseWorkCountry'] !== 'Switzerland' &&
                values['maritalStatusDetails.spousalIncome'],
        },
        {
            key: 'maritalStatusDetails.spouseStartedWorkingFrom',
            type: ShownTypes.date,
            label: 'Start date',
            condition: (values) => values.maritalStatus === 'Married' && values['maritalStatusDetails.spousalIncome'],
        },
        // {
        //     key: 'maritalStatusDetails.spouseWorkPermit',
        //     type: ShownTypes.select,
        //     label: 'Does your spouse has a C work permit?',
        //     popoverText: 'We need to know this information to determine your tax-at-source.',
        //     options: ['Yes', 'No'].map((item) => {
        //         return {
        //             label: item,
        //             value: item,
        //         };
        //     }),
        //     condition: (values) =>
        //         values.maritalStatus === 'Married' &&
        //         values['maritalStatusDetails.spousalIncome'] &&
        //         values['maritalStatusDetails.isSpouseSwissResident'] &&
        //         values['maritalStatusDetails.spouseWorkCountry'] === 'Switzerland' &&
        //         values['maritalStatusDetails.spouseNationality'] !== 'Switzerland',
        // },
        // {
        //     key: 'maritalStatusDetails.spouseWorkPermitDocuments',
        //     type: ShownTypes.uploadFile,
        //     label: 'Spouse work permit document',
        //     span: 24,
        //     condition: (values) =>
        //         values.maritalStatus === 'Married' &&
        //         values['maritalStatusDetails.spousalIncome'] &&
        //         values['maritalStatusDetails.isSpouseSwissResident'] &&
        //         values['maritalStatusDetails.spouseWorkCountry'] === 'Switzerland' &&
        //         values['maritalStatusDetails.spouseNationality'] !== 'Switzerland',
        // },
    ],
    [FieldKeys.expensesForOptimization]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        //TODO
        // <Popover
        //className="info-pop-button"
        //placement="rightTop"
        //content={<p>Receipt mandatory</p>}
        //trigger="hover"
        //overlayClassName="info-pop-content"
        //showArrow={false}>
        //<button>
        //<WarningIcon />
        //</button>
        //</Popover>
        {
            key: 'useExpensesForOptimization',
            type: ShownTypes.select,
            label: 'Expenses for optimization',
            options: ['Yes', 'No'].map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 24,
            showAsterick: true,
        },
    ],
    [FieldKeys.mealAllowance]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'useMealAllowance',
            type: ShownTypes.select,
            label: 'Meal Allowance',
            options: ['Yes', 'No'].map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 24,
            showAsterick: true,
        },
    ],
    [FieldKeys.paymentCurrency]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'paymentCurrency',
            type: ShownTypes.select,
            label: 'Payment currency',
            options: currencies.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 24,
            showAsterick: true,
        },
    ],
    [FieldKeys.treasury]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'treasuryPaymentDate',
            type: ShownTypes.select,
            label: 'Payment date',
            options: ['ASAP', '25th day of the month'].map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
        },
    ],
    [FieldKeys.representativeAllowance]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'hasRepresentativeAllowance',
            type: ShownTypes.select,
            label: 'Representative allowance',
            options: ['Yes', 'No'].map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 24,
            showAsterick: true,
        },
        {
            key: 'representativeAmount',
            type: ShownTypes.input,
            label: 'Representative amount CHF',
            span: 24,
            inputType: 'number',
            condition: (values) => values.hasRepresentativeAllowance === 'Yes',
        },
    ],
    [FieldKeys.bankAccount]: [
        // Row 1
        { key: 'startDate', type: ShownTypes.date, label: 'Start date', span: 12 },
        { key: 'endDate', type: ShownTypes.date, label: 'End date', span: 12 },
        // Row 2
        {
            key: 'accountDetailsLabel',
            type: ShownTypes.label,
            span: 24,
            label: 'Account details',
        },
        // Row 3.1
        {
            key: 'hasBankAccount',
            type: ShownTypes.radio,
            label: 'Do you have a bank account?',
            span: 24,
            isSeporateRadio: true,
        },
        // Row 3.2
        {
            key: 'hasIBAN',
            type: ShownTypes.radio,
            label: 'Do you have an IBAN number?',
            condition: (values) => values.hasBankAccount === true,
            span: 24,
            isSeporateRadio: true,
        },
        // Row 4.1
        {
            key: 'iban',
            type: ShownTypes.input,
            label: 'IBAN number',
            condition: (values) => values.hasBankAccount === true && values.hasIBAN === true,
            span: 12,
        },
        {
            key: 'swift',
            type: ShownTypes.input,
            label: 'Swift',
            condition: (values) => values.hasBankAccount === true && values.hasIBAN === true,
            span: 6,
        },
        {
            key: 'currency',
            type: ShownTypes.select,
            label: 'Currency',
            options: currency.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },
        // Row 4.2
        {
            key: 'bankAccountDetails',
            type: ShownTypes.input,
            label: 'Bank account details',
            condition: (values) => values.hasBankAccount === true && values.hasIBAN === false,
            span: 24,
        },

        {
            key: 'ownerDetailsLabel',
            type: ShownTypes.label,
            span: 24,
            label: 'Account owner',
            condition: (values) => values.hasBankAccount === true,
        },
        // Row 5
        {
            key: 'accountOwner',
            type: ShownTypes.input,
            label: 'Owner of the account',
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },

        // Row 6
        {
            key: 'addressName',
            type: ShownTypes.input,
            label: 'Address street name',
            condition: (values) => values.hasBankAccount === true,
            span: 12,
        },
        {
            key: 'addressNumber',
            type: ShownTypes.input,
            label: 'Address street number',
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },
        {
            key: 'additionalAddress',
            type: ShownTypes.input,
            label: 'Address line 2',
            condition: (values) => values.hasBankAccount === true,
            span: 24,
        },
        // Row 7
        {
            key: 'comment',
            type: ShownTypes.input,
            label: 'Comment',
            condition: (values) => values.hasBankAccount === true,
            span: 24,
        },
        // Row 8
        {
            key: 'bankDetailsLabel',
            type: ShownTypes.label,
            span: 24,
            label: 'Bank details',
            condition: (values) => values.hasBankAccount === true,
        },
        // Row 9
        {
            key: 'bankName',
            type: ShownTypes.input,
            label: 'Name of the bank',
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },
        {
            key: 'bankAddressName',
            type: ShownTypes.input,
            label: 'Address street name',
            condition: (values) => values.hasBankAccount === true,
            span: 12,
        },
        {
            key: 'bankAddressNumber',
            type: ShownTypes.input,
            label: 'Address street number',
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },
        {
            key: 'bankAdditionalAddress',
            type: ShownTypes.input,
            label: 'Address line 2',
            condition: (values) => values.hasBankAccount === true,
            span: 24,
        },
        {
            key: 'npa',
            type: ShownTypes.input,
            label: 'Postcode of the Bank',
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },
        {
            key: 'bankCity',
            type: ShownTypes.input,
            label: 'City bank',
            condition: (values) => values.hasBankAccount === true,
            span: 12,
        },
        {
            key: 'bankCountry',
            type: ShownTypes.input,
            label: 'Country bank',
            options: countries.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            condition: (values) => values.hasBankAccount === true,
            span: 6,
        },
        // Row 12
        {
            key: 'bankChangeOption',
            type: ShownTypes.checkbox,
            label: 'Bank change option',
            condition: (values) => false,
        },
        {
            key: 'dateOfBankChange',
            type: ShownTypes.date,
            label: 'Date of bank change',
            condition: (values) => false,
        },
    ],
    [FieldKeys.lossOfIncome]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'lossOfIncome5050',
            type: ShownTypes.switch,
            label: 'Loss of Income 50-50',
            span: 24,
            condition: () => store.getState().auth.role === Roles.ADMIN || store.getState().auth.role === Roles.MANAGER,
        },
        {
            key: 'incomeLossInsurance',
            type: ShownTypes.select,
            label: 'Loss of income insurance',
            options: insurances.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
        },
        {
            key: 'lossOfIncome',
            type: ShownTypes.input,
            label: 'Loss of income %',
            inputType: 'number',
        },
    ],
    [FieldKeys.cba]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'cbaOption',
            type: ShownTypes.select,
            label: 'CBA/CCT option',
            options: ['0.80 % ER', '0.40% - 0.40% EE/ER'].map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
        },
        {
            key: 'isCctOfficial',
            type: ShownTypes.radio,
            label: 'Are you CCT official?',
            isSeporateRadio: true,
            span: 24,
            condition: () => store.getState().auth.role === Roles.ADMIN || store.getState().auth.role === Roles.MANAGER,
        },
    ],
    [FieldKeys.accidentRate]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'accidentInsurance5050',
            type: ShownTypes.switch,
            label: 'Accident Insurance 50-50',
            span: 24,
            condition: () => store.getState().auth.role === Roles.ADMIN || store.getState().auth.role === Roles.MANAGER,
        },
        {
            key: 'professionalAccident',
            type: ShownTypes.input,
            inputType: 'number',
            label: 'Professional accident %',
            span: 12,
        },
        {
            key: 'nonProfessionalAccident',
            type: ShownTypes.input,
            inputType: 'number',
            label: 'Non-professional accident %',
            span: 12,
        },
    ],
    [FieldKeys.otherJob]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'isPrimaryJob',
            type: ShownTypes.radio,
            label: 'Is it a primary job?',
            isSeporateRadio: true,
            span: 24,
        },
        {
            key: 'numeriqTime',
            type: ShownTypes.input,
            inputType: 'number',
            label: '% of time you work for Numeriq',
            span: 24,
        },
    ],
    [FieldKeys.workTime]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'numeriqTimeTotal',
            type: ShownTypes.input,
            inputType: 'number',
            label: 'Total % of Work Time',
            span: 24,
        },
    ],
    [FieldKeys.workTimeContract]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'numeriqTime',
            type: ShownTypes.input,
            inputType: 'number',
            label: '% of time of work (for this contract)',
            span: 24,
        },
    ],
    [FieldKeys.invoiceCurrency]: [
        { key: 'startDate', type: ShownTypes.date, label: 'Start date' },
        { key: 'endDate', type: ShownTypes.date, label: 'End date' },
        {
            key: 'invoiceCurrency',
            type: ShownTypes.select,
            label: 'Invoice currency',
            options: currencies.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 12,
        },
        {
            key: 'paymentCurrency',
            type: ShownTypes.select,
            label: 'Payment currency',
            optionsCondition: (values) => {
                const invoiceCurrency = values.invoiceCurrency;
                return currencies
                    .filter(
                        (value) =>
                            (value === 'CHF' && value === invoiceCurrency) ||
                            value === 'CHF' ||
                            value === invoiceCurrency
                    )
                    .map((item) => ({
                        label: item,
                        value: item,
                    }));
            },
            span: 12,
        },
        {
            key: 'bankAccount',
            type: ShownTypes.select,
            label: 'Bank account',
            options: store.getState().onboarding.bankAccounts.map((item: IBankAccount) => {
                return {
                    value: item._id,
                    label: `${item.name} (${item.currency})`,
                };
            }),
            condition: () => store.getState().auth.role === Roles.ADMIN || store.getState().auth.role === Roles.MANAGER,
            span: 12,
        },
    ],
    [FieldKeys.workPermit]: [
        {
            key: 'startDate',
            type: ShownTypes.date,
            label: 'Start date',
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
        },
        {
            key: 'endDate',
            type: ShownTypes.date,
            label: 'End date',
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
        },
        {
            key: 'workPermitLabel',
            type: ShownTypes.label,
            span: 24,
            label: 'Information',
        },
        {
            key: 'hasWorkPermit',
            type: ShownTypes.radio,
            label: 'Do you have a work permit?',
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            span: 24,
            isSeporateRadio: true,
        },
        {
            key: 'selfHandledWorkPermit',
            type: ShownTypes.radio,
            label: 'Did you start the work permit process by yourself?',
            span: 24,
            condition: (values) => !values.hasWorkPermit,
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            isSeporateRadio: true,
        },
        {
            key: 'hasStartedWorkPermitProcess',
            type: ShownTypes.radio,
            label: 'Did you start the process with town hall/canton?',
            span: 24,
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            condition: (values) => !values.hasWorkPermit,
            isSeporateRadio: true,
        },
        {
            key: 'hasProofOfOngoingProcess',
            type: ShownTypes.radio,
            label: 'Proof of ongoing process',
            span: 24,
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            condition: (values) =>
                (values.hasStartedWorkPermitProcess || values.selfHandledWorkPermit) && !values.hasWorkPermit,
            isSeporateRadio: true,
        },
        //   {hasStartedWorkPermitProcess && !hasWorkPermit && !isSwissNationality && (
        //<span style={{ fontSize: '14px' }}>
        //   Please upload a proof of registration/application
        // </span>
        //)}
        {
            key: 'documentType',
            type: ShownTypes.select,
            label: 'Document type',
            disabled: (values) =>
                !values.hasWorkPermit || store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            options: documentTypes.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 6,
        },
        {
            key: 'permitType',
            type: ShownTypes.select,
            label: 'Permit type',
            disabled: (values) =>
                !values.hasWorkPermit || store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            options: permitTypes
                .filter((item) => item !== 'Passport')
                .map((item) => {
                    return {
                        label: item,
                        value: item,
                    };
                }),
            span: 6,
        },
        {
            key: 'permitCanton',
            type: ShownTypes.select,
            label: 'Permit canton (where you live or intend to live)',
            disabled: (values) =>
                !values.hasWorkPermit || store.getState().onboarding.personalInfo.nationality === 'Switzerland',
            options: cantons.map((item) => {
                return {
                    label: item,
                    value: item,
                };
            }),
            span: 6,
        },
        {
            key: 'basisOfLegalStay',
            type: ShownTypes.select,
            label: 'Basis of legal stay',
            disabled: (values) =>
                values.selfHandledWorkPermit ||
                (store.getState().onboarding.personalInfo.nationality !== 'Switzerland' &&
                !values.hasWorkPermit &&
                !values.selfHandledWorkPermit
                    ? false
                    : store.getState().onboarding.personalInfo.nationality === 'Switzerland'),
            optionsCondition: (values: any) =>
                !values.isSwissNationality && !values.hasWorkPermit && !values.selfHandledWorkPermit
                    ? basisOfLegalStayValues
                          .filter((item) => item === 'Work Abroad & Live Abroad')
                          .map((item) => {
                              return {
                                  label: item,
                                  value: item,
                              };
                          })
                    : basisOfLegalStayValues.map((item) => {
                          return {
                              label: item,
                              value: item,
                          };
                      }),
            span: 6,
        },
        // {
        //   key: 'isReligious',
        //   type: ShownTypes.radio,
        //   label: 'Religion',
        //   span: 24,
        //   popoverText:
        //     'As you are subject to source tax, the authorities ask whether you are religious. If this is the case, you will be subject to church tax.',
        // },
        {
            key: 'documents',
            type: ShownTypes.uploadFile,
            label: 'Documents',
            span: 24,
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
        },
        {
            key: 'comment',
            type: ShownTypes.input,
            label: 'Comment',
            span: 24,
            disabled: () => store.getState().onboarding.personalInfo.nationality === 'Switzerland',
        },
    ],
};
